import React from 'react';
import Head from 'next/head';
import NextLink from 'next/link';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  UnorderedList,
  ListItem,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { getPageTitle, getMetaDescription } from '../src/lib/config';

export default function TermsOfServicePage() {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const headingColor = useColorModeValue('gray.900', 'white');

  return (
    <>
      <Head>
        <title>{getPageTitle('利用規約')}</title>
        <meta name="description" content={getMetaDescription('利用規約')} />
      </Head>

      <Box bg={bgColor} minH="100vh" py={12}>
        <Container maxW="3xl" px={4}>
          <VStack spacing={8} align="stretch">
            <Box textAlign="center">
              <Link as={NextLink} href="/" fontWeight="semibold" fontSize="lg" _hover={{ textDecoration: 'none' }}>
                LogMe
              </Link>
            </Box>

            <Heading as="h1" size="xl" color={headingColor} textAlign="center">
              利用規約
            </Heading>

            <Text color={textColor} fontSize="sm" textAlign="right">
              制定日：2026年3月29日
            </Text>

            <Divider />

            <VStack spacing={6} align="stretch" color={textColor} lineHeight="tall">
              <Text>
                本利用規約（以下「本規約」）は、LogMe（以下「当サービス」）の利用条件を定めるものです。
                ユーザーは、本規約に同意のうえ当サービスをご利用ください。
              </Text>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第1条（適用）
                </Heading>
                <Text>
                  本規約は、当サービスの利用に関する一切の関係に適用されます。
                  当サービスが別途定める個別規定やガイドラインも、本規約の一部を構成します。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第2条（アカウント登録）
                </Heading>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>ユーザーは、正確かつ最新の情報を提供してアカウント登録を行うものとします。</ListItem>
                  <ListItem>アカウントの管理責任はユーザーにあり、第三者への貸与・共有は禁止します。</ListItem>
                  <ListItem>登録情報に虚偽があった場合、当サービスはアカウントを停止・削除できるものとします。</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第3条（サービス内容）
                </Heading>
                <Text>
                  当サービスは、ナイトワーク店舗向けの業務管理サービスを提供します。
                  サービスの内容は、事前の通知なく変更・追加・廃止される場合があります。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第4条（禁止事項）
                </Heading>
                <Text mb={2}>ユーザーは、以下の行為を行ってはなりません。</Text>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>法令または公序良俗に反する行為</ListItem>
                  <ListItem>当サービスの運営を妨害する行為</ListItem>
                  <ListItem>他のユーザーまたは第三者の権利を侵害する行為</ListItem>
                  <ListItem>不正アクセスまたはそれに類する行為</ListItem>
                  <ListItem>当サービスの逆コンパイル・リバースエンジニアリング</ListItem>
                  <ListItem>当サービスを利用した反社会的活動</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第5条（料金・支払い）
                </Heading>
                <Text>
                  有料プランの料金・支払い条件は、別途定める料金表に従います。
                  無料トライアル期間中は料金が発生しません。
                  料金の変更がある場合は、事前に通知します。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第6条（データの取扱い）
                </Heading>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>ユーザーが入力したデータの所有権はユーザーに帰属します。</ListItem>
                  <ListItem>当サービスは、サービス提供に必要な範囲でデータを利用します。</ListItem>
                  <ListItem>アカウント削除後のデータ保持期間は、別途定めるところに従います。</ListItem>
                  <ListItem>個人情報の取扱いについては、プライバシーポリシーに従います。</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第7条（サービスの中断・終了）
                </Heading>
                <Text>
                  当サービスは、以下の場合にサービスの全部または一部を中断・終了できるものとします。
                </Text>
                <UnorderedList spacing={1} pl={4} mt={2}>
                  <ListItem>システムの保守・更新を行う場合</ListItem>
                  <ListItem>天災・障害等の不可抗力による場合</ListItem>
                  <ListItem>その他、運営上やむを得ない場合</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第8条（免責事項）
                </Heading>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>当サービスは、サービスの完全性・正確性・継続性を保証するものではありません。</ListItem>
                  <ListItem>当サービスの利用により生じた損害について、当サービスに故意または重過失がある場合を除き、責任を負いません。</ListItem>
                  <ListItem>ユーザー間またはユーザーと第三者間のトラブルについて、当サービスは一切関与しません。</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第9条（規約の変更）
                </Heading>
                <Text>
                  当サービスは、必要に応じて本規約を変更できるものとします。
                  重要な変更がある場合は、サービス内またはメールにてお知らせします。
                  変更後も当サービスを継続利用した場合、変更後の規約に同意したものとみなします。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  第10条（準拠法・管轄）
                </Heading>
                <Text>
                  本規約は日本法に準拠し、本規約に関する紛争については、
                  東京地方裁判所を第一審の専属的合意管轄裁判所とします。
                </Text>
              </Box>
            </VStack>

            <Divider />

            <Text textAlign="center" fontSize="sm" color="gray.500">
              <Link as={NextLink} href="/" _hover={{ textDecoration: 'underline' }}>
                LogMe トップページへ戻る
              </Link>
            </Text>
          </VStack>
        </Container>
      </Box>
    </>
  );
}
